/**
 * PPE auto-detection (V2CORE-2901) shared types for the V1 dashboard.
 *
 * Every shape here mirrors a HOME `v2/...` response. New backend fields are
 * optional so the dashboard keeps working against an older backend.
 */

/** Capture / floor detection status, as sent by HOME (`PPEDetectionStatus`). */
export type PpeDetectionStatus =
  | 'NOT_DETECTED'
  | 'DETECTION_IN_PROGRESS'
  | 'DETECTED'
  | 'PARTIALLY_DETECTED'
  | 'DETECTION_FAILED';

export const PPE_DETECTION_STATUSES: readonly PpeDetectionStatus[] = [
  'NOT_DETECTED',
  'DETECTION_IN_PROGRESS',
  'DETECTED',
  'PARTIALLY_DETECTED',
  'DETECTION_FAILED',
];

/** Error codes HOME puts in `error_code` for PPE refusals. */
export type PpeRefusalCode =
  | 'PPE_NOT_SUBSCRIBED'
  | 'PPE_ORG_DISABLED'
  | 'PPE_NOT_LAST_LEVEL_PROJECT'
  | 'PPE_ALLOWANCE_REACHED'
  | 'PPE_FORBIDDEN'
  | 'PPE_RUN_IN_PROGRESS'
  | 'PPE_BUSY'
  | 'PPE_NOT_ACTIVE'
  | 'PPE_NO_ELIGIBLE_FLOOR'
  | 'PPE_RERUN_LIMIT'
  /** Manual run on several captures (V2CORE-3363): a capture of the project was removed. */
  | 'PPE_CAPTURE_REMOVED'
  /** Manual run on several captures (V2CORE-3363): the run failed for one capture on the server. */
  | 'PPE_RUN_FAILED';

/** Why a re-run is not possible right now (`rerun_blocked_reason`). */
export type PpeRerunBlockedReason =
  | 'PPE_NOT_ACTIVE'
  | 'PPE_RUN_IN_PROGRESS'
  | 'PPE_NO_ELIGIBLE_FLOOR'
  | 'PPE_RERUN_LIMIT';

/** Why a floor was not queued by a re-run (`floors_skipped[].reason`). */
export type PpeFloorSkipReason = 'FACE_BLUR_FAILED' | 'BLUR_IN_PROGRESS' | 'NO_POINTS';

/** `GET|PUT v2/projects/{id}/ppe-detection` -> `data`. */
export interface ProjectPpeState {
  enabled: boolean;
  subscribed: boolean;
  used: number;
  allowed: number;
  /** fe-admin additive field: the project can hold a credit. */
  last_level?: boolean;
  /** fe-admin additive field: max(0, allowed - used). */
  available?: number;
  /** fe-admin additive field: the caller may switch it (same gate as the PUT). */
  can_toggle?: boolean;
  /** Additive: the org admin's own switch in Admin Controls → Privileges (V2CORE-3460). */
  org_enabled?: boolean;
}

export interface PpeFloorCounts {
  total: number;
  detected: number;
  failed: number;
  in_progress: number;
  /** Only on the per-report GET. */
  not_eligible?: number;
}

/** One item of `POST v2/reports/ppe-detection-status` -> `data[]`. */
export interface ReportPpeStatusItem {
  report_id: string;
  status: PpeDetectionStatus | null;
  ai_observations_count?: number;
  floors?: PpeFloorCounts;
  /** rerun additive field. */
  ai_observations_removed_at?: string | null;
  /** True when the id is unknown or not viewable by the caller. */
  not_found?: boolean;
}

/** `GET v2/reports/{report}/ppe-detection` -> `data`. */
export interface ReportPpeDetail {
  report_id: string;
  status: PpeDetectionStatus;
  detection_active: boolean;
  ai_observations: {
    total: number;
    removable: number;
    kept_edited: number;
    kept_pushed: number;
  };
  floors: PpeFloorCounts;
  last_requested_at: string | null;
  ai_observations_removed_at: string | null;
  can_rerun: boolean;
  can_remove_all: boolean;
  rerun_blocked_reason: PpeRerunBlockedReason | null;
}

/** `POST v2/reports/{report}/ppe-detection/rerun` (202) -> `data`. */
export interface RerunResult {
  report_id: string;
  /** The run row id (sent by HOME, optional for older answers). */
  run_id?: string;
  status: PpeDetectionStatus;
  floors_queued: number;
  floors_skipped: { report_floor_id: string; reason: PpeFloorSkipReason }[];
}

/** `POST v2/reports/{report}/ppe-detection/remove-all` (200) -> `data`. */
export interface RemoveAllResult {
  report_id: string;
  removed: number;
  kept_pushed: number;
  ai_observations_removed_at: string | null;
}

/**
 * `GET v2/projects/{id}/ppe-detection/manual-run` -> `data` (V2CORE-3363): whether the report
 * list may offer "Run AI detection" on several captures.
 */
export interface ProjectPpeManualRunState {
  project_id: string;
  detection_active: boolean;
  not_active_reason: string | null;
  /** Includes the RUN_PPE_DETECTION check and whether detection is active. */
  can_run: boolean;
  /** PPE_FORBIDDEN or PPE_NOT_ACTIVE when `can_run` is false. */
  blocked_reason: string | null;
  /** Most captures one POST may name. */
  max_reports: number;
}

/** One capture the manual run did not queue, with the refusal code (`PPE_RUN_IN_PROGRESS`, ...). */
export interface PpeManualRunSkipped {
  report_id: string;
  reason: string;
}

/** `POST v2/projects/{id}/ppe-detection/manual-run` (200) -> `data`. */
export interface PpeManualRunResult {
  project_id: string;
  /** Each item has the one-capture re-run answer shape. */
  queued: RerunResult[];
  skipped: PpeManualRunSkipped[];
  queued_count: number;
  skipped_count: number;
}

/** Max report ids per manual-run call (HOME `ManualRunPpeDetectionRequest`). */
export const PPE_MANUAL_RUN_LIMIT = 100;

/** Standard HOME V2 envelope. */
export interface PpeApiResponse<T> {
  status: string;
  message: string;
  error_code?: string | null;
  meta_data?: unknown;
  data: T;
  errors?: unknown;
}

/** Max report ids per batch status call (HOME `PpeStatusBatchRequest`). */
export const PPE_STATUS_BATCH_LIMIT = 100;

/** Poll period while any capture on screen is in progress. */
export const PPE_STATUS_POLL_INTERVAL_MS = 10_000;

/** i18n key for each status label. */
export const PPE_STATUS_I18N_KEYS: Record<PpeDetectionStatus, string> = {
  NOT_DETECTED: 'ppe.status.NOT_DETECTED',
  DETECTION_IN_PROGRESS: 'ppe.status.DETECTION_IN_PROGRESS',
  DETECTED: 'ppe.status.DETECTED',
  PARTIALLY_DETECTED: 'ppe.status.PARTIALLY_DETECTED',
  DETECTION_FAILED: 'ppe.status.DETECTION_FAILED',
};

/** i18n key for each refusal / error code. */
export const PPE_ERROR_I18N_KEYS: Record<PpeRefusalCode, string> = {
  PPE_NOT_SUBSCRIBED: 'ppe.error.PPE_NOT_SUBSCRIBED',
  PPE_ORG_DISABLED: 'ppe.error.PPE_ORG_DISABLED',
  PPE_NOT_LAST_LEVEL_PROJECT: 'ppe.error.PPE_NOT_LAST_LEVEL_PROJECT',
  PPE_ALLOWANCE_REACHED: 'ppe.error.PPE_ALLOWANCE_REACHED',
  PPE_FORBIDDEN: 'ppe.error.PPE_FORBIDDEN',
  PPE_RUN_IN_PROGRESS: 'ppe.error.PPE_RUN_IN_PROGRESS',
  PPE_BUSY: 'ppe.error.PPE_BUSY',
  PPE_NOT_ACTIVE: 'ppe.error.PPE_NOT_ACTIVE',
  PPE_NO_ELIGIBLE_FLOOR: 'ppe.error.PPE_NO_ELIGIBLE_FLOOR',
  PPE_RERUN_LIMIT: 'ppe.error.PPE_RERUN_LIMIT',
  PPE_CAPTURE_REMOVED: 'ppe.error.PPE_CAPTURE_REMOVED',
  PPE_RUN_FAILED: 'ppe.error.PPE_RUN_FAILED',
};

/** Fallback i18n key for an unknown error. */
export const PPE_GENERIC_ERROR_I18N_KEY = 'ppe.error.generic';

/** i18n key for each re-run floor skip reason. */
export const PPE_FLOOR_SKIP_I18N_KEYS: Record<PpeFloorSkipReason, string> = {
  FACE_BLUR_FAILED: 'ppe.rerun.skip.FACE_BLUR_FAILED',
  BLUR_IN_PROGRESS: 'ppe.rerun.skip.BLUR_IN_PROGRESS',
  NO_POINTS: 'ppe.rerun.skip.NO_POINTS',
};

/** Normalises any backend value to one of the 5 statuses (unknown / null -> NOT_DETECTED). */
export function normalizePpeStatus(status: unknown): PpeDetectionStatus {
  return PPE_DETECTION_STATUSES.includes(status as PpeDetectionStatus)
    ? (status as PpeDetectionStatus)
    : 'NOT_DETECTED';
}

/** i18n key for an error code; unknown codes give the generic message key. */
export function ppeErrorI18nKey(code: string | null | undefined): string {
  return (code && PPE_ERROR_I18N_KEYS[code as PpeRefusalCode]) || PPE_GENERIC_ERROR_I18N_KEY;
}

/**
 * Confidence as a whole percentage, or null when missing.
 * Accepts a 0..1 fraction (AI contract) or an already-scaled 0..100 value.
 */
export function ppeConfidencePercent(
  confidence: number | string | null | undefined,
): number | null {
  if (confidence === null || confidence === undefined || confidence === '') {
    return null;
  }
  const value = Number(confidence);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  const percent = value <= 1 ? value * 100 : value;
  return Math.round(Math.min(percent, 100));
}

/** HOME sends booleans as true/false, 1/0 or "1"/"0" depending on the resource. */
export function isAiGenerated(row: { is_ai_generated?: unknown } | null | undefined): boolean {
  const flag = row?.is_ai_generated;
  return flag === true || flag === 1 || flag === '1' || flag === 'true';
}

/**
 * Why a user reports an AI observation as incorrect (V2CORE-3078).
 * Mirrors HOME `IncorrectAIDetectionTypes`; the int is what the API takes.
 */
export enum PpeIncorrectReason {
  Incorrect = 0,
  FalsePositive = 1,
  ImageQuality = 2,
  Other = 3,
}

/** Reasons in the order the report dialog lists them. */
export const PPE_INCORRECT_REASONS: readonly PpeIncorrectReason[] = [
  PpeIncorrectReason.Incorrect,
  PpeIncorrectReason.FalsePositive,
  PpeIncorrectReason.ImageQuality,
  PpeIncorrectReason.Other,
];

/** i18n key for each report reason. */
export const PPE_INCORRECT_REASON_I18N_KEYS: Record<PpeIncorrectReason, string> = {
  [PpeIncorrectReason.Incorrect]: 'ppe.ai.report.reason.incorrect',
  [PpeIncorrectReason.FalsePositive]: 'ppe.ai.report.reason.false_positive',
  [PpeIncorrectReason.ImageQuality]: 'ppe.ai.report.reason.image_quality',
  [PpeIncorrectReason.Other]: 'ppe.ai.report.reason.other',
};

/** Max length of the report justification (HOME validation). */
export const PPE_REPORT_JUSTIFICATION_MAX = 500;

/** Body of `POST v2/observations/{id}/detection-feedback`. */
export interface PpeReportIncorrectRequest {
  reason_code: PpeIncorrectReason;
  /** Required by HOME only when `reason_code` is Other; omitted when empty. */
  justification?: string;
}

/**
 * `POST v2/observations/{id}/detection-feedback` -> `data`.
 * 201 for a new report; 200 with `already_reported: true` when this user reported it before.
 */
export interface PpeReportIncorrectResult {
  id: string | number;
  delivery_status: string;
  already_reported: boolean;
}

/**
 * Optional read field on observation details and list rows: the current user already
 * reported this AI observation. Missing (older backend) means not reported.
 */
export function isReportedIncorrect(
  row: { reported_incorrect?: unknown } | null | undefined,
): boolean {
  const flag = row?.reported_incorrect;
  return flag === true || flag === 1 || flag === '1' || flag === 'true';
}
